import { BugoBrandBrief } from '@shared/api/bugoBrand/bugoBrand.interface';
import { FeventOne } from '@shared/api/fevent/fevent.interface';
import { FeventChangeWorkerDto } from '@shared/api/fevent/feventWorkerChange.controller';
import { User } from '@shared/api/user/user.interface';
import { UserRole } from '@shared/types';

/**
 * 담당 지도사 변경(관리자 전용) — 순수 계산부.
 *
 * 상가(fevent)의 담당 지도사를 바꾸면 그 상가에 딸린 bugo·member의 user도 전부
 * 새 지도사로 따라가야 한다. 안 그러면 새 지도사가 부고 재전송·상주 수정에서
 * owningFilter(worker=bugo.user) 밖이라 401로 막힌다(bugoAttribution.util 참고).
 * 새 지도사의 상조(bugoBrand)가 다르면 fevent.bugoBrand·bugo.bugoBrand도 함께 바꾼다.
 *
 * 스펙: docs/fevent-change-worker-spec.md
 */

//* 검색 결과(admin/user populate 축약형). bugoBrand는 populate되면 객체, 아니면 id 문자열
export type WorkerCandidate = User<BugoBrandBrief | string, undefined, BugoBrandBrief>;

//* 담당 지도사로 지정 가능한 role — fevent.user 로 실릴 수 있는 계정
export const WORKER_ASSIGNABLE_ROLES: UserRole[] = [
  UserRole.BugoAgencyWorker,
  UserRole.BugoAgencyBranchAdmin,
  UserRole.BugoAgencyAdmin,
];

export const brandIdOf = (brand?: BugoBrandBrief | string | null): string | undefined => {
  if (!brand) return undefined;
  return typeof brand === 'string' ? brand : brand._id;
};

/** 후보 계정이 속한 상조 — 상조관리자는 소속 브랜드 목록의 첫 번째(feventAddEdit.util 생성 규칙과 동일) */
export const candidateBrand = (
  user: Pick<WorkerCandidate, 'roles' | 'bugoBrand' | 'bugoAgencyAdminDetail'>,
): BugoBrandBrief | string | undefined => {
  if (user.roles?.includes(UserRole.BugoAgencyAdmin)) {
    return user.bugoAgencyAdminDetail?.bugoBrands?.[0] ?? user.bugoBrand ?? undefined;
  }
  return user.bugoBrand ?? undefined;
};

export const candidateBrandName = (brand?: BugoBrandBrief | string | null): string => {
  if (!brand || typeof brand === 'string') return '';
  return brand.bugoAgency?.nickName ?? brand.bugoAgency?.corpName ?? '';
};

export const feventUserIdOf = (fevent: Pick<FeventOne, 'user'>): string | undefined => {
  const user = fevent.user as User | string | undefined;
  if (!user) return undefined;
  return typeof user === 'string' ? user : user._id;
};

/** 변경 계획 — 화면 미리보기·확인 문구·실제 요청 body의 단일 출처 */
export interface WorkerChangePlan {
  feventId: string;
  fromUserId?: string;
  toUserId: string;
  fromBrandId?: string;
  toBrandId?: string;
  /** 새 지도사의 상조가 현재 상가의 상조와 다름 → fevent·bugo의 bugoBrand도 교체 */
  brandChanged: boolean;
  /** 최종 bugoBrandAgencyIndex. 상조가 바뀌면 옛 index는 무의미하므로 새로 고른 값(없으면 undefined=해제) */
  bugoBrandAgencyIndex?: number;
  /** 새 상조가 멀티 브랜드(bugoAgencyList)인데 아직 행사 상조를 안 골랐음 → 확인 전 선택 필요 */
  needsAgencyIndex: boolean;
  /** 같은 지도사·같은 상조면 실행할 게 없음 */
  noop: boolean;
  memberCount: number;
  bugoCount: number;
}

const agencyListOf = (brand?: BugoBrandBrief | string | null) =>
  !brand || typeof brand === 'string' ? [] : brand.bugoAgencyList ?? [];

export const buildWorkerChangePlan = (
  fevent: FeventOne,
  target: WorkerCandidate,
  chosenAgencyIndex?: number,
): WorkerChangePlan => {
  const fromUserId = feventUserIdOf(fevent);
  const fromBrandId = brandIdOf(fevent.bugoBrand as unknown as BugoBrandBrief | string);
  const toBrand = candidateBrand(target);
  const toBrandId = brandIdOf(toBrand);
  //* 새 지도사에게 상조가 없으면(비정상 계정) 상가 상조는 그대로 둔다
  const brandChanged = Boolean(toBrandId) && toBrandId !== fromBrandId;

  const members = fevent.memberOrderList ?? [];
  const bugoCount = members.filter((member) => Boolean(member?.bugo)).length;

  const newAgencyList = brandChanged ? agencyListOf(toBrand) : [];
  const bugoBrandAgencyIndex = brandChanged
    ? chosenAgencyIndex
    : fevent.bugoBrandAgencyIndex;
  const needsAgencyIndex =
    brandChanged && newAgencyList.length > 0 && typeof chosenAgencyIndex !== 'number';

  return {
    feventId: fevent._id,
    fromUserId,
    toUserId: target._id,
    fromBrandId,
    toBrandId,
    brandChanged,
    bugoBrandAgencyIndex,
    needsAgencyIndex,
    noop: fromUserId === target._id && !brandChanged,
    memberCount: members.length,
    bugoCount,
  };
};

/** 서버 요청 body — 계획에서 그대로 파생 */
export const toChangeWorkerDto = (plan: WorkerChangePlan): FeventChangeWorkerDto => ({
  userId: plan.toUserId,
  ...(plan.brandChanged && typeof plan.bugoBrandAgencyIndex === 'number'
    ? { bugoBrandAgencyIndex: plan.bugoBrandAgencyIndex }
    : {}),
});
